import React from 'react'
import { t } from '../../../i18n'
import Icon from '../../shared/icon'
import StatusIndicatorBadge from '../../shared/StatusIndicatorBadge'
import { CommandButton, ControlGroupKey, LockerStateIndicator } from '../types'
import ActionButton from './ActionButton'

const scope = 'controls'

interface Props {
  groupKey: ControlGroupKey
  commands: CommandButton[]
  lockerStates: LockerStateIndicator[]
}

const groupByLockerNumber = (buttons: CommandButton[]): Record<number, CommandButton[]> => {
  const groups: Record<number, CommandButton[]> = {}
  buttons.forEach(button => {
    const lockerNumber = Number(button.params.locker_number)
    if (!lockerNumber) return
    groups[lockerNumber] ||= []
    groups[lockerNumber].push(button)
  })
  return groups
}

// The door is orthogonal to the state, so it is appended rather than replacing the explanation —
// except while closing, where an open door is the state.
const stateDetail = (lockerState: LockerStateIndicator): string => {
  const detail = t(`locker_state_tooltips.${lockerState.state}`, { scope })
  if (!lockerState.door_open || lockerState.state === 'cooling_down') return detail

  return `${detail} · ${t('locker_state_tooltips.door_open', { scope })}`
}

const LockerLayout = ({ groupKey, commands, lockerStates }: Props) => {
  const lockerOpenCloseCommands = commands.filter(c => c.command === 'open_locker' || c.command === 'close_locker')
  const openAllCommand  = commands.find(c => c.command === 'open_all_lockers')  || null
  const closeAllCommand = commands.find(c => c.command === 'close_all_lockers') || null

  const buttonsByLocker = groupByLockerNumber(lockerOpenCloseCommands)
  const lockerNumbers = lockerStates.length > 0
    ? lockerStates.map(lockerState => lockerState.locker_number)
    : Object.keys(buttonsByLocker).map(Number).sort((a, b) => a - b)

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6 className="card-title mb-3">{t(`groups.${groupKey}`, { scope })}</h6>
        {commands.length === 0 ? (
          <div className="text-muted fst-italic">—</div>
        ) : (
          <>
            <div className="controls-actions-grid-default mb-3">
              {openAllCommand && (
                <ActionButton
                  command={openAllCommand}
                  style={{ gridColumn: '-3' }}
                  buttonClassName={'btn btn-primary w-100 h-100 d-inline-flex align-items-center justify-content-center gap-2 controls-actions-btn-tall'}
                  icon={<Icon name="lockOpen" color="currentColor" />}
                />
              )}
              {closeAllCommand && (
                <ActionButton
                  command={closeAllCommand}
                  style={{ gridColumn: '-2' }}
                  buttonClassName={'btn btn-primary w-100 h-100 d-inline-flex align-items-center justify-content-center gap-2 controls-actions-btn-tall'}
                  icon={<Icon name="lockClosed" color="currentColor" />}
                />
              )}
            </div>

            <div className="controls-actions-grid-default">
              {lockerNumbers.map(lockerNumber => {
                const lockerState = lockerStates.find(state => state.locker_number === lockerNumber)

                return (
                  <div key={lockerNumber} className="controls-locker-cell">
                    {lockerState && (
                      <StatusIndicatorBadge
                        label={t(`locker_states.${lockerState.state}`, { scope })}
                        level={lockerState.level}
                        detail={stateDetail(lockerState)}
                        size="sm"
                        colorOk={true}
                      />
                    )}
                    {(buttonsByLocker[lockerNumber] ?? []).map(c => <ActionButton key={c.name} command={c} />)}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LockerLayout
